import { DateParserService } from "./../../../../services/shared/DateParserService.service";
// @angular
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  effect,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
import { Subscription } from "rxjs";
// Spinner
import { NgxSpinnerService } from "ngx-spinner";
// Own services
import { SessionDataService } from "../../../../services/shared/SessionDataService.service";
import { NotificationModalService } from "../../../../modules/notification-module/notification-service/notification-modal.service";
import { ThemeService } from "../../../../themes/theme.service";
import { MatMenuTrigger, MatMenuModule } from "@angular/material/menu";
// Variables
import { PROFILES } from "../../../../../assets/profiles/profiles";
import { LANGUAGE } from "../../../../services/language/LanguageController.service";
import { TranslateModule } from "@ngx-translate/core";
import { ScreenRecorderComponent } from "../../../../global/screen-recorder/screen-recorder.component";
import { Router, RouterLink } from "@angular/router";
import { MatIconModule } from "@angular/material/icon";
import { NgClass } from "@angular/common";
import { Agrupation } from "../../../../interfaces/AgrupationGlobalInterface.type";
import { ENVIRONMENT } from "../../../../../environments/environment";
import { isPresentationModeRestrictedRoute } from "../../../../services/shared/PresentationModeGuard.service";

/**
 * User menu of the top navbar: preferences, account actions, notifications, logout, and the ARSON presentation-mode toggle.
 * Changes are emitted as outputs for the navbar to apply.
 */
@Component({
  selector: "app-navbar-user-dropdown",
  templateUrl: "./navbar-user-dropdown.component.html",
  styleUrls: ["./navbar-user-dropdown.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatMenuModule,
    NgClass,
    MatIconModule,
    RouterLink,
    ScreenRecorderComponent,
    TranslateModule
  ]
})
export class NavbarUserDropdownComponent implements OnInit, OnDestroy {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  @Input() sessionUserName: { name: string; surname: string };
  @Input() sessionProfile: string;
  currentLanguage: string;
  mainPage: string;
  sessionTheme: string;
  currentAgrupation: Agrupation;
  @Input() languageName: string;
  @Input() allowList: boolean;
  @Input() disableLog: boolean;
  @Input() pendingNotifications: number;
  currentUserId: number;
  originalProfile: string;
  numberFormat: string;
  dateFormat: string;
  recordData: { fileName: string };
  userRestrictionsActive: boolean = true;
  userSuspicionActive: boolean = false;
  readonly PROFILES = PROFILES;
  readonly LANGUAGE = LANGUAGE;
  readonly ENVIRONMENT = ENVIRONMENT;

  @Output() switchLanguageFlag = new EventEmitter<string>();
  @Output() switchNumberFormatFlag = new EventEmitter<string>();
  @Output() switchDateFormatFlag = new EventEmitter<string>();
  @Output() logoutFlag = new EventEmitter<any>();
  @Output() reloginFlag = new EventEmitter<any>();
  @Output() deleteUserLocalSettingsFlag = new EventEmitter<any>();
  @Output() resetMapSettingsFlag = new EventEmitter<void>();
  @Output() updateUserRestrictionsFlag = new EventEmitter<boolean>();
  @Output() updateUserSuspicionFlag = new EventEmitter<boolean>();
  @Output() updateMainPageFlag = new EventEmitter<string>();

  @ViewChild("arsonUserMenuTrigger") arsonUserMenuTrigger: MatMenuTrigger;
  arsonMenuActive: boolean = false;
  @ViewChild("localUserMenuTrigger") localUserMenuTrigger: MatMenuTrigger;
  localUserMenuActive: boolean = false;
  @ViewChild("userLogMenuTrigger") userLogMenuTrigger: MatMenuTrigger;
  userLogMenuActive: boolean = false;
  @ViewChild("themeMenuTrigger") themeMenuTrigger: MatMenuTrigger;
  @ViewChild("userMenuTrigger") userMenuTrigger: MatMenuTrigger;
  userMenuActive: boolean = false;
  @ViewChild("cameraUserMenuTrigger") cameraUserMenuTrigger: MatMenuTrigger;
  cameraMenuActive: boolean = false;
  lastLogin: number;
  lastLoginParsed: string;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    public notificationModal: NotificationModalService,
    private readonly SessionDataService: SessionDataService,
    private readonly spinner: NgxSpinnerService,
    private readonly theme: ThemeService,
    private readonly DateParserService: DateParserService,
    private readonly router: Router
  ) {
    effect(() => {
      const dateFormat = this.SessionDataService.dateFormat();
      if (dateFormat) {
        this.dateFormat = dateFormat;
        this.parseLoginDate();
      }
    });
    effect(() => {
      const lastLogin = this.SessionDataService.lastLoginDate();
      if (lastLogin) {
        this.lastLogin = lastLogin;
        this.parseLoginDate();
      }
    });
    effect(() => {
      const agrupation = this.SessionDataService.agrupation();
      if (agrupation) {
        this.currentAgrupation = agrupation;
        this.parseLoginDate();
      }
    });
    effect(() => {
      const originalProfile = this.SessionDataService.originalProfile();
      if (originalProfile !== null) {
        this.originalProfile = originalProfile;
      }
    });
    effect(() => {
      const numberFormat = this.SessionDataService.numberFormat();
      if (numberFormat) {
        this.numberFormat = numberFormat;
      }
    });
  }

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    this.currentLanguage = untracked(() => this.SessionDataService.language());
    this.mainPage = untracked(() => this.SessionDataService.mainPage());
    this.sessionTheme = this.theme.getActiveTheme()?.name;
    this.currentUserId = untracked(() => this.SessionDataService.userId());
    this.currentAgrupation = untracked(() => this.SessionDataService.agrupation());
    this.originalProfile = untracked(() => this.SessionDataService.originalProfile());
    this.dateFormat = untracked(() => this.SessionDataService.dateFormat());
    this.numberFormat = untracked(() => this.SessionDataService.numberFormat());
    this.lastLogin = untracked(() => this.SessionDataService.lastLoginDate());
    if (this.lastLogin) this.parseLoginDate();

    this.recordData = {
      fileName: untracked(() => this.SessionDataService.agrupation())?.name
    };
  }

  parseLoginDate(): void {
    if (this.lastLogin && this.dateFormat && this.currentAgrupation) {
      this.lastLoginParsed = this.DateParserService.parseDate(
        this.lastLogin,
        this.dateFormat + " HH:mm:ss",
        this.currentAgrupation.timezone
      );
    }
  }

  get presentationModeActive(): boolean {
    return this.SessionDataService.presentationModeActive();
  }

  /***************************************************************************/
  // ANCHOR Component destruction
  /***************************************************************************/

  ngOnDestroy(): void {}

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  // Language change
  switchLanguage(language: string): void {
    this.currentLanguage = language;
    this.switchLanguageFlag.emit(language);
  }

  // Number format change
  switchFormatNumber(format: string): void {
    this.switchNumberFormatFlag.emit(format);
  }

  // Date format change
  switchFormatDate(format: string): void {
    this.switchDateFormatFlag.emit(format);
  }

  // User logout
  logout(): void {
    this.logoutFlag.emit();
  }

  relogin(): void {
    this.reloginFlag.emit();
  }

  // Delete local settings
  deleteUserLocalSettings(): void {
    this.deleteUserLocalSettingsFlag.emit();
  }

  // Reset map settings to defaults
  resetMapSettings(): void {
    this.resetMapSettingsFlag.emit();
  }

  // Update style theme
  updateTheme(theme: string): void {
    this.theme.setTheme(theme);
    this.theme.saveThemeConfig(theme);
    this.sessionTheme = theme;
    this.SessionDataService.sendReloadPanelFlag();
  }

  // Update user restrictions
  updateUserRestrictions(): void {
    this.updateUserRestrictionsFlag.emit(this.userRestrictionsActive);
  }

  // Update suspicion visibility
  updateUserSuspicionActive(): void {
    this.updateUserSuspicionFlag.emit(this.userSuspicionActive);
  }

  // Update profile
  updateProfile(newProfile: string): void {
    this.userRestrictionsActive = true;
    this.updateUserRestrictions();
    this.SessionDataService.sendProfile(newProfile);
  }

  togglePresentationMode(): void {
    const activating = !this.SessionDataService.presentationModeActive();
    this.SessionDataService.togglePresentationMode();
    if (activating && isPresentationModeRestrictedRoute(this.router.url)) {
      void this.router.navigateByUrl("/principal");
    }
  }

  // Open game
  openGame(): void {
    this.SessionDataService.sendGameActive(true);
    setTimeout(() => this.spinner.show("spinner-hard"), 0);
  }

  // Menus
  closeUserMenu(): void {
    setTimeout(() => {
      if (
        !this.userMenuActive &&
        !this.arsonMenuActive &&
        !this.localUserMenuActive &&
        !this.userLogMenuActive &&
        !this.cameraMenuActive
      ) {
        this.userMenuTrigger?.closeMenu();
      }
    }, 200);
  }

  // Main page
  updateMainPage(mainPage: string): void {
    this.mainPage = mainPage;
    this.updateMainPageFlag.emit(mainPage);
  }
}
